"use client";

import { useState } from "react";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";
import AppLogo from "@/components/ui/AppLogo";

type StatusType = "Submitted" | "Under Review" | "Approved" | "Requires Revision";

interface Submission {
  id: string;
  productName: string;
  county: string;
  category: string;
  submittedBy: string;
  organization: string;
  submittedDate: string;
  status: StatusType;
  images: number;
  image: string;
  imageAlt: string;
  priority: boolean;
}

const SUBMISSIONS: Submission[] = [
{
  id: "KTP-2026-0052",
  productName: "Maasai Mara Hot Air Balloon Safari",
  county: "Narok",
  category: "Adventure Tourism",
  submittedBy: "James Olekulel",
  organization: "Narok County Tourism Board",
  submittedDate: "Feb 22, 2026",
  status: "Submitted",
  images: 8,
  image: "https://images.unsplash.com/photo-1663462968744-8cbf62e12c7c",
  imageAlt: "Hot air balloon floating above Maasai Mara savanna at sunrise Kenya",
  priority: true
},
{
  id: "KTP-2026-0051",
  productName: "Fort Jesus Night Tours",
  county: "Mombasa",
  category: "Cultural Heritage",
  submittedBy: "Fatuma Hassan",
  organization: "National Museums of Kenya",
  submittedDate: "Feb 21, 2026",
  status: "Under Review",
  images: 6,
  image: "https://images.unsplash.com/photo-1714645418379-95230139038c",
  imageAlt: "Fort Jesus stone walls illuminated at night with coastal Mombasa backdrop",
  priority: false
},
{
  id: "KTP-2026-0050",
  productName: "Rusinga Island Fishing & Culture Tour",
  county: "Homa Bay",
  category: "Community-Based Tourism",
  submittedBy: "Otieno Ochieng",
  organization: "Rusinga Island CBO",
  submittedDate: "Feb 20, 2026",
  status: "Approved",
  images: 7,
  image: "https://images.unsplash.com/photo-1512427106889-c9937ec60a4d",
  imageAlt: "Traditional fishing boats on Lake Victoria near Rusinga Island Kenya",
  priority: false
},
{
  id: "KTP-2026-0049",
  productName: "Lake Turkana Jade Sea Experience",
  county: "Turkana",
  category: "Eco-Tourism",
  submittedBy: "Naomi Ekiru",
  organization: "Turkana County Tourism Office",
  submittedDate: "Feb 19, 2026",
  status: "Requires Revision",
  images: 3,
  image: "https://images.unsplash.com/photo-1636341694159-da3b3550baaa",
  imageAlt: "Turquoise waters of Lake Turkana stretching to volcanic island shores in northern Kenya",
  priority: false
},
{
  id: "KTP-2026-0048",
  productName: "Kakamega Forest Birding Safaris",
  county: "Kakamega",
  category: "Eco-Tourism",
  submittedBy: "Samuel Wafula",
  organization: "Kakamega Forest Tourism CBO",
  submittedDate: "Feb 18, 2026",
  status: "Approved",
  images: 9,
  image: "https://images.unsplash.com/photo-1709826457342-d905947c09bc",
  imageAlt: "Dense tropical rainforest canopy of Kakamega Forest with rare bird species Kenya",
  priority: false
},
{
  id: "KTP-2026-0047",
  productName: "Lamu Dhow Sailing Experience",
  county: "Lamu",
  category: "Beach Tourism",
  submittedBy: "Amina Mohammed",
  organization: "Lamu Tourism Association",
  submittedDate: "Feb 17, 2026",
  status: "Under Review",
  images: 5,
  image: "https://images.unsplash.com/photo-1570821409025-bf5444843f83",
  imageAlt: "Traditional Swahili dhow sailing on turquoise Indian Ocean waters near Lamu Island Kenya",
  priority: true
},
{
  id: "KTP-2026-0046",
  productName: "Amboseli Maasai Cultural Village",
  county: "Kajiado",
  category: "Cultural Heritage",
  submittedBy: "Lilian Nkadiku",
  organization: "Amboseli Maasai Community Trust",
  submittedDate: "Feb 16, 2026",
  status: "Approved",
  images: 11,
  image: "https://images.unsplash.com/photo-1606676834127-a2b5a7d4126d",
  imageAlt: "Maasai warriors in traditional red shukas with Mount Kilimanjaro visible in background Kenya",
  priority: false
},
{
  id: "KTP-2026-0045",
  productName: "Diani Beach Watersports Hub",
  county: "Kwale",
  category: "Beach Tourism",
  submittedBy: "Brian Mwangi",
  organization: "Kwale Coast Tourism Enterprise",
  submittedDate: "Feb 15, 2026",
  status: "Submitted",
  images: 6,
  image: "https://images.unsplash.com/photo-1570582569027-0b283b1816f0",
  imageAlt: "Crystal clear turquoise water at Diani Beach with white sand and palm trees Kenya",
  priority: false
}];


const STATUS_CONFIG: Record<StatusType, {label: string;className: string;icon: string;}> = {
  "Submitted": { label: "Submitted", className: "status-submitted", icon: "InboxIcon" },
  "Under Review": { label: "Under Review", className: "status-review", icon: "ClockIcon" },
  "Approved": { label: "Approved", className: "status-approved", icon: "CheckBadgeIcon" },
  "Requires Revision": { label: "Requires Revision", className: "status-revision", icon: "ExclamationTriangleIcon" }
};

const NAV_ITEMS = [
{ icon: "Squares2X2Icon", label: "Dashboard", id: "dashboard" },
{ icon: "InboxIcon", label: "Submissions", id: "submissions" },
{ icon: "CheckBadgeIcon", label: "Approved", id: "approved" },
{ icon: "ClockIcon", label: "Under Review", id: "review" },
{ icon: "ExclamationTriangleIcon", label: "Needs Revision", id: "revision" },
{ icon: "MapPinIcon", label: "By County", id: "county" },
{ icon: "ChartBarIcon", label: "Analytics", id: "analytics" },
{ icon: "Cog6ToothIcon", label: "Settings", id: "settings" }];


const COUNTY_STATS = [
{ county: "Narok", count: 142, pct: 95 },
{ county: "Mombasa", count: 118, pct: 79 },
{ county: "Kwale", count: 97, pct: 65 },
{ county: "Lamu", count: 84, pct: 56 },
{ county: "Nairobi", count: 76, pct: 51 },
{ county: "Turkana", count: 61, pct: 41 },
{ county: "Kakamega", count: 58, pct: 39 },
{ county: "Kajiado", count: 52, pct: 35 }];


export default function AdminDashboardClient() {
  const [activeNav, setActiveNav] = useState("dashboard");
  const [filterStatus, setFilterStatus] = useState<StatusType | "All">("All");
  const [filterCategory, setFilterCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [submissions, setSubmissions] = useState<Submission[]>(SUBMISSIONS);

  const updateStatus = (id: string, newStatus: StatusType) => {
    setSubmissions((prev) =>
    prev.map((s) => s.id === id ? { ...s, status: newStatus } : s)
    );
    if (selectedSubmission?.id === id) {
      setSelectedSubmission((prev) => prev ? { ...prev, status: newStatus } : null);
    }
  };

  const filtered = submissions.filter((s) => {
    const matchStatus = filterStatus === "All" || s.status === filterStatus;
    const matchCategory = filterCategory === "All" || s.category === filterCategory;
    const matchSearch =
    !searchQuery ||
    s.productName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.county.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.submittedBy.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.id.toLowerCase().includes(searchQuery.toLowerCase());
    return matchStatus && matchCategory && matchSearch;
  });

  const stats = {
    total: submissions.length,
    submitted: submissions.filter((s) => s.status === "Submitted").length,
    review: submissions.filter((s) => s.status === "Under Review").length,
    approved: submissions.filter((s) => s.status === "Approved").length,
    revision: submissions.filter((s) => s.status === "Requires Revision").length
  };

  const categories = ["All", ...Array.from(new Set(submissions.map((s) => s.category)))];

  return (
    <div className="flex h-screen bg-warm-bg overflow-hidden">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-kenya-black text-white flex flex-col transition-transform duration-300 ${
        sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:relative lg:translate-x-0`
        }>
        
        {/* Kenya stripe top */}
        <div className="h-[4px] flex flex-shrink-0">
          <div className="flex-1 bg-kenya-black border-t border-white/10" />
          <div className="flex-1 bg-kenya-red" />
          <div className="flex-1 bg-kenya-green" />
        </div>

        {/* Logo */}
        <div className="p-6 border-b border-white/8 flex-shrink-0">
          <AppLogo size={34} text="County Product Portal" className="brightness-0 invert" />
          <p className="text-[0.6875rem] font-bold uppercase tracking-[0.3em] text-white/35 mt-2 font-condensed">
            Admin Portal · Ministry of Tourism
          </p>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          {NAV_ITEMS.map((item) =>
          <button
            key={item.id}
            onClick={() => {setActiveNav(item.id);setSidebarOpen(false);}}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-[0.9375rem] font-medium transition-all admin-nav-item ${
            activeNav === item.id ? "active" : "text-white/45 hover:text-white/75"}`
            }>
            
              <Icon name={item.icon as Parameters<typeof Icon>[0]["name"]} size={19} />
              {item.label}
              {item.id === "submissions" && stats.submitted > 0 &&
            <span className="ml-auto bg-kenya-red text-white text-[0.6875rem] font-bold px-2 py-0.5 rounded-full">
                  {stats.submitted}
                </span>
            }
            </button>
          )}
        </nav>

        {/* Admin profile */}
        <div className="p-4 border-t border-white/8 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-kenya-red/20 border border-kenya-red/35 flex items-center justify-center text-kenya-red font-bold text-[0.9375rem]">
              CM
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[0.875rem] font-bold text-white truncate">Caroline Mwangi</p>
              <p className="text-[0.75rem] text-white/35 truncate">Senior Tourism Officer</p>
            </div>
            <Icon name="EllipsisVerticalIcon" size={17} className="text-white/35 flex-shrink-0" />
          </div>
        </div>
      </aside>

      {/* Sidebar overlay (mobile) */}
      {sidebarOpen &&
      <div className="fixed inset-0 z-30 bg-kenya-black/65 lg:hidden" onClick={() => setSidebarOpen(false)} />
      }

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="bg-white border-b-2 border-kenya-black/8 px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3 sm:gap-4">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-1.5 rounded-lg hover:bg-kenya-black/6 transition-colors">
              <Icon name="Bars3Icon" size={21} className="text-kenya-black" />
            </button>
            <div>
              <h1 className="text-[0.875rem] sm:text-[0.9375rem] font-bold text-kenya-black capitalize font-display">{activeNav === "dashboard" ? "Dashboard Overview" : activeNav}</h1>
              <p className="text-[0.625rem] sm:text-[0.6875rem] text-text-muted uppercase tracking-widest font-condensed hidden sm:block">Feb 24, 2026 · 09:41 EAT</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-kenya-green/10 border border-kenya-green/20">
              <div className="w-2 h-2 rounded-full bg-kenya-green pulse-dot" />
              <span className="text-[0.6875rem] font-bold text-kenya-green uppercase tracking-widest font-condensed">Live</span>
            </div>
            <button className="relative p-2 rounded-lg hover:bg-kenya-black/6 transition-colors">
              <Icon name="BellIcon" size={19} className="text-text-muted" />
              <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-kenya-red" />
            </button>
          </div>
        </header>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6">

          {/* Stats cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {[
            { label: "Total Submissions", value: stats.total, icon: "InboxIcon", color: "kenya-black", sub: "+12 this week" },
            { label: "Under Review", value: stats.review, icon: "ClockIcon", color: "amber", sub: "Avg 3.2 days" },
            { label: "Approved", value: stats.approved, icon: "CheckBadgeIcon", color: "kenya-green", sub: "94% approval rate" },
            { label: "Needs Revision", value: stats.revision, icon: "ExclamationTriangleIcon", color: "kenya-red", sub: "Action required" }].
            map((stat) =>
            <div key={stat.label} className="bg-white rounded-xl2 border-2 border-kenya-black/8 p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[0.6875rem] font-bold uppercase tracking-[0.25em] text-text-muted font-condensed">{stat.label}</span>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                stat.color === "kenya-green" ? "bg-kenya-green/10" :
                stat.color === "kenya-red" ? "bg-kenya-red/10" :
                stat.color === "amber" ? "bg-amber-50" : "bg-kenya-black/6"}`
                }>
                    <Icon
                    name={stat.icon as Parameters<typeof Icon>[0]["name"]}
                    size={17}
                    className={
                    stat.color === "kenya-green" ? "text-kenya-green" :
                    stat.color === "kenya-red" ? "text-kenya-red" :
                    stat.color === "amber" ? "text-amber-500" : "text-kenya-black"
                    } />
                  
                  </div>
                </div>
                <div>
                  <div className="text-3xl font-display font-bold text-kenya-black">{stat.value}</div>
                  <div className="text-[0.8125rem] text-text-muted mt-0.5">{stat.sub}</div>
                </div>
              </div>
            )}
          </div>

          <div className="grid lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Submissions table */}
            <div className="lg:col-span-2 bg-white rounded-xl2 border-2 border-kenya-black/8 overflow-hidden">
              {/* Table header */}
              <div className="p-4 sm:p-5 border-b-2 border-kenya-black/8">
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  {/* Search */}
                  <div className="relative flex-1">
                    <Icon name="MagnifyingGlassIcon" size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                    <input
                      type="text"
                      placeholder="Search submissions..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-4 py-2.5 bg-warm-bg border-2 border-kenya-black/10 rounded-lg text-[0.9375rem] text-kenya-black placeholder:text-text-muted/50 focus:outline-none focus:border-kenya-green/50" />
                    
                  </div>
                  {/* Status filter */}
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value as StatusType | "All")}
                    className="py-2.5 px-3 bg-warm-bg border-2 border-kenya-black/10 rounded-lg text-[0.9375rem] text-kenya-black focus:outline-none focus:border-kenya-green/50 appearance-none cursor-pointer">
                    
                    <option value="All">All Status</option>
                    <option value="Submitted">Submitted</option>
                    <option value="Under Review">Under Review</option>
                    <option value="Approved">Approved</option>
                    <option value="Requires Revision">Requires Revision</option>
                  </select>
                  {/* Category filter */}
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="py-2.5 px-3 bg-warm-bg border-2 border-kenya-black/10 rounded-lg text-[0.9375rem] text-kenya-black focus:outline-none focus:border-kenya-green/50 appearance-none cursor-pointer">
                    
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-kenya-black/8">
                      <th className="text-left px-5 py-3.5 text-[0.6875rem] font-bold uppercase tracking-[0.3em] text-text-muted font-condensed">Product</th>
                      <th className="text-left px-4 py-3.5 text-[0.6875rem] font-bold uppercase tracking-[0.3em] text-text-muted font-condensed hidden md:table-cell">County</th>
                      <th className="text-left px-4 py-3.5 text-[0.6875rem] font-bold uppercase tracking-[0.3em] text-text-muted font-condensed hidden lg:table-cell">Date</th>
                      <th className="text-left px-4 py-3.5 text-[0.6875rem] font-bold uppercase tracking-[0.3em] text-text-muted font-condensed">Status</th>
                      <th className="text-left px-4 py-3.5 text-[0.6875rem] font-bold uppercase tracking-[0.3em] text-text-muted font-condensed">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((sub) => {
                      const statusCfg = STATUS_CONFIG[sub.status];
                      return (
                        <tr
                          key={sub.id}
                          className={`border-b border-kenya-black/5 hover:bg-warm-bg/70 transition-colors cursor-pointer ${
                          selectedSubmission?.id === sub.id ? "bg-kenya-red/[0.02]" : ""}`
                          }
                          onClick={() => setSelectedSubmission(sub)}>
                          
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-11 h-11 rounded-lg overflow-hidden flex-shrink-0 bg-warm-surface">
                                <AppImage
                                  src={sub.image}
                                  alt={sub.imageAlt}
                                  width={44}
                                  height={44}
                                  className="w-full h-full object-cover" />
                                
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-1.5">
                                  {sub.priority &&
                                  <span className="w-2 h-2 rounded-full bg-kenya-red flex-shrink-0" />
                                  }
                                  <p className="text-[0.9375rem] font-bold text-kenya-black truncate max-w-[160px]">{sub.productName}</p>
                                </div>
                                <p className="text-[0.75rem] text-text-muted font-mono">{sub.id}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4 hidden md:table-cell">
                            <span className="text-[0.875rem] font-medium text-text-muted">{sub.county}</span>
                          </td>
                          <td className="px-4 py-4 hidden lg:table-cell">
                            <span className="text-[0.875rem] text-text-muted">{sub.submittedDate}</span>
                          </td>
                          <td className="px-4 py-4">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[0.6875rem] font-bold uppercase tracking-wider ${statusCfg.className}`}>
                              <Icon name={statusCfg.icon as Parameters<typeof Icon>[0]["name"]} size={11} />
                              <span className="hidden sm:inline">{statusCfg.label}</span>
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={(e) => {e.stopPropagation();setSelectedSubmission(sub);}}
                                className="p-1.5 rounded-lg hover:bg-kenya-black/6 transition-colors"
                                title="View details">
                                
                                <Icon name="EyeIcon" size={15} className="text-text-muted" />
                              </button>
                              <button
                                onClick={(e) => {e.stopPropagation();updateStatus(sub.id, "Approved");}}
                                className="p-1.5 rounded-lg hover:bg-kenya-green/12 transition-colors"
                                title="Approve">
                                
                                <Icon name="CheckIcon" size={15} className="text-kenya-green" />
                              </button>
                              <button
                                onClick={(e) => {e.stopPropagation();updateStatus(sub.id, "Requires Revision");}}
                                className="p-1.5 rounded-lg hover:bg-kenya-red/12 transition-colors"
                                title="Request revision">
                                
                                <Icon name="PencilSquareIcon" size={15} className="text-kenya-red" />
                              </button>
                            </div>
                          </td>
                        </tr>);

                    })}
                  </tbody>
                </table>

                {filtered.length === 0 &&
                <div className="py-16 text-center">
                    <Icon name="InboxIcon" size={34} className="text-text-muted mx-auto mb-3 opacity-40" />
                    <p className="text-[0.9375rem] font-bold text-text-muted">No submissions match your filters</p>
                  </div>
                }
              </div>

              {/* Table footer */}
              <div className="px-5 py-3.5 border-t-2 border-kenya-black/8 flex items-center justify-between">
                <p className="text-[0.875rem] text-text-muted">
                  Showing <strong className="text-kenya-black">{filtered.length}</strong> of <strong className="text-kenya-black">{submissions.length}</strong> submissions
                </p>
                <div className="flex items-center gap-2">
                  <button className="p-1.5 rounded-lg border-2 border-kenya-black/10 hover:bg-warm-bg transition-colors">
                    <Icon name="ChevronLeftIcon" size={15} className="text-text-muted" />
                  </button>
                  <span className="text-[0.875rem] font-bold text-kenya-black px-2">1</span>
                  <button className="p-1.5 rounded-lg border-2 border-kenya-black/10 hover:bg-warm-bg transition-colors">
                    <Icon name="ChevronRightIcon" size={15} className="text-text-muted" />
                  </button>
                </div>
              </div>
            </div>

            {/* Right panel */}
            <div className="space-y-4">
              {/* Detail panel */}
              {selectedSubmission ?
              <div className="bg-white rounded-xl2 border-2 border-kenya-black/8 overflow-hidden">
                  <div className="relative aspect-video">
                    <AppImage
                    src={selectedSubmission.image}
                    alt={selectedSubmission.imageAlt}
                    fill
                    className="object-cover"
                    sizes="400px" />
                  
                    <div className="absolute inset-0 bg-gradient-to-t from-kenya-black/85 to-transparent" />
                    <button
                    onClick={() => setSelectedSubmission(null)}
                    className="absolute top-3 right-3 w-8 h-8 rounded-full bg-kenya-black/65 flex items-center justify-center hover:bg-kenya-black transition-colors">
                    
                      <Icon name="XMarkIcon" size={15} className="text-white" />
                    </button>
                    <div className="absolute bottom-3 left-3">
                      <p className="font-bold text-white text-[0.9375rem] leading-tight">{selectedSubmission.productName}</p>
                      <p className="text-white/55 text-[0.75rem] font-mono">{selectedSubmission.id}</p>
                    </div>
                  </div>
                  <div className="p-5 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      {[
                    { label: "County", value: selectedSubmission.county },
                    { label: "Category", value: selectedSubmission.category },
                    { label: "Submitted By", value: selectedSubmission.submittedBy },
                    { label: "Images", value: `${selectedSubmission.images} uploaded` },
                    { label: "Date", value: selectedSubmission.submittedDate },
                    { label: "Organization", value: selectedSubmission.organization }].
                    map((item) =>
                    <div key={item.label}>
                          <p className="text-[0.6875rem] font-bold uppercase tracking-widest text-text-muted font-condensed">{item.label}</p>
                          <p className="text-[0.875rem] font-semibold text-kenya-black mt-0.5 leading-tight">{item.value}</p>
                        </div>
                    )}
                    </div>

                    {/* Current status */}
                    <div className="flex items-center justify-between py-3 border-t-2 border-kenya-black/8">
                      <span className="text-[0.6875rem] font-bold uppercase tracking-widest text-text-muted font-condensed">Current Status</span>
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[0.6875rem] font-bold ${STATUS_CONFIG[selectedSubmission.status].className}`}>
                        <Icon name={STATUS_CONFIG[selectedSubmission.status].icon as Parameters<typeof Icon>[0]["name"]} size={11} />
                        {selectedSubmission.status}
                      </span>
                    </div>

                    {/* Action buttons */}
                    <div className="space-y-2">
                      <p className="text-[0.6875rem] font-bold uppercase tracking-widest text-text-muted font-condensed">Update Status</p>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                        onClick={() => updateStatus(selectedSubmission.id, "Under Review")}
                        className="py-2.5 px-3 rounded-lg text-[0.75rem] font-bold uppercase tracking-wider bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors">
                        
                          Under Review
                        </button>
                        <button
                        onClick={() => updateStatus(selectedSubmission.id, "Approved")}
                        className="py-2.5 px-3 rounded-lg text-[0.75rem] font-bold uppercase tracking-wider bg-kenya-green/12 text-kenya-green hover:bg-kenya-green/22 transition-colors">
                        
                          &#10003; Approve
                        </button>
                        <button
                        onClick={() => updateStatus(selectedSubmission.id, "Requires Revision")}
                        className="py-2.5 px-3 rounded-lg text-[0.75rem] font-bold uppercase tracking-wider bg-kenya-red/10 text-kenya-red hover:bg-kenya-red/18 transition-colors">
                        
                          Needs Revision
                        </button>
                        <button
                        className="py-2.5 px-3 rounded-lg text-[0.75rem] font-bold uppercase tracking-wider bg-kenya-black/6 text-kenya-black hover:bg-kenya-black/12 transition-colors flex items-center justify-center gap-1">
                        
                          <Icon name="ArrowDownTrayIcon" size={12} />
                          Export PDF
                        </button>
                      </div>
                    </div>
                  </div>
                </div> :

              <div className="bg-white rounded-xl2 border-2 border-kenya-black/8 p-8 text-center space-y-3">
                  <div className="w-12 h-12 rounded-xl bg-kenya-black/6 flex items-center justify-center mx-auto">
                    <Icon name="CursorArrowRaysIcon" size={23} className="text-text-muted" />
                  </div>
                  <p className="text-[0.9375rem] font-bold text-kenya-black">Select a submission</p>
                  <p className="text-[0.875rem] text-text-muted">Click any row to view details and manage status</p>
                </div>
              }

              {/* County breakdown */}
              <div className="bg-white rounded-xl2 border-2 border-kenya-black/8 p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-[0.6875rem] font-bold uppercase tracking-[0.3em] text-text-muted font-condensed">Top Counties</h3>
                  <span className="text-[0.6875rem] font-bold text-kenya-red uppercase tracking-widest font-condensed">By Submissions</span>
                </div>
                <div className="space-y-3">
                  {COUNTY_STATS.map((item) =>
                  <div key={item.county} className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[0.875rem] font-semibold text-kenya-black">{item.county}</span>
                        <span className="text-[0.875rem] font-bold text-text-muted">{item.count}</span>
                      </div>
                      <div className="h-2 bg-kenya-black/6 rounded-full overflow-hidden">
                        <div
                        className="h-full rounded-full bg-gradient-to-r from-kenya-red to-kenya-green"
                        style={{ width: `${item.pct}%` }} />
                      
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Quick actions */}
              <div className="bg-kenya-black rounded-xl2 p-5 space-y-3">
                <h3 className="text-[0.6875rem] font-bold uppercase tracking-[0.3em] text-white/45 font-condensed">Quick Actions</h3>
                <div className="space-y-2">
                  {[
                  { icon: "ArrowDownTrayIcon", label: "Export All Approved", color: "text-kenya-green" },
                  { icon: "EnvelopeIcon", label: "Email All Pending", color: "text-blue-400" },
                  { icon: "DocumentTextIcon", label: "Generate Report", color: "text-white/65" },
                  { icon: "GlobeAltIcon", label: "Push to Portal", color: "text-kenya-red" }].
                  map((action) =>
                  <button
                    key={action.label}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white/6 hover:bg-white/12 transition-colors text-left">
                    
                      <Icon name={action.icon as Parameters<typeof Icon>[0]["name"]} size={16} className={action.color} />
                      <span className="text-[0.875rem] font-medium text-white/65">{action.label}</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>);

}